import type { CaseRecord, RecordFact } from "../types";

// Derive a clean, deduplicated party list from verified facts + record
// from/to addresses. Handles the messy real-world values the model and
// email headers produce: emails, phone-number prefixes, "Last, First"
// ordering, "Org / Person" combos, abbreviations, and spelling variants.

export interface PartyCard {
  name: string;
  org?: string;
  role?: string;
}

const GENERIC = new Set([
  "sender", "recipient", "representative", "facility", "customer", "staff",
  "agent", "unknown", "user", "owner", "the sender", "me", "external", "cc",
]);

const PERSONAL_DOMAINS = new Set([
  "gmail.com", "comcast.net", "yahoo.com", "outlook.com", "hotmail.com",
  "icloud.com", "aol.com", "me.com", "live.com", "msn.com",
]);

// Email domain → canonical organisation name.
const DOMAIN_ORG: Record<string, string> = {
  "ilsos.gov": "Illinois Secretary of State",
  "libertymutual.com": "Liberty Mutual Insurance",
  "chicagopolice.org": "Chicago Police Department",
  "enterprise.com": "Enterprise",
};

// Whole-name aliases (lower-cased, exact match) → canonical name.
const ALIASES: Record<string, string> = {
  cpd: "Chicago Police Department",
  ilsos: "Illinois Secretary of State",
  platerevocations: "Illinois Secretary of State",
  "liberty mutual": "Liberty Mutual Insurance",
};

const STOP_TOKENS = new Set(["the", "and", "of", "inc", "llc", "co", "dept"]);

// Tokens too generic to merge two ORG names on their own: "Liberty Mutual
// Insurance" and "Safeco Insurance Company of Oregon" share "insurance" but
// are different insurers on the same claim (Round 9, 2C failing strings).
const ORG_GENERIC_TOKENS = new Set([
  "insurance", "company", "corporation", "agency", "group", "claims",
  "service", "services", "department",
]);
const SMALL_WORDS = new Set(["of", "the", "and", "for", "to", "a", "an"]);

// Values that are channel/placeholder noise, never a party ("Email" as a
// party came from the model echoing the source type — AND from
// orgFromEmail deriving an org name from a domain like "email.com").
const NON_PARTY = new Set(["email", "sms", "phone", "text", "unknown", "n/a", "na", "none"]);

// Placeholder/anonymous attributions ("Unknown thief", "Unidentified
// driver"). Valid FACT attributions — they stay on the fact cards — but
// they never mint parties-list entries regardless of how many records
// carry them: the parties list is for identifiable participants (Round 9).
const PLACEHOLDER_RE = /^(unknown|unidentified|unnamed|anonymous)\b/i;

// Shared placeholder test for every surface that aggregates party values
// (rollup "parties:" lines, timeline party arrays, chips). Per-fact
// attribution lines keep showing the stored value — dropping it would
// un-attribute the fact — but aggregate lists never include placeholders.
export function isPlaceholderParty(value: string | null | undefined): boolean {
  const v = (value ?? "").trim();
  if (!v) return true;
  return PLACEHOLDER_RE.test(v) || GENERIC.has(v.toLowerCase()) || isNonParty(v);
}

// Robust membership: strip everything but letters/digits/slash so "E-mail",
// "EMAIL:", and "N / A" all match their canonical entries.
function isNonParty(value: string): boolean {
  return NON_PARTY.has(value.toLowerCase().replace(/[^a-z0-9/]+/g, ""));
}

function titleCase(s: string): string {
  // A fully shouting name ("JORDAN LEE") title-cases entirely; otherwise
  // short ALL-CAPS tokens are preserved as acronyms ("Denver PD" must not
  // become "Denver Pd").
  const allCaps = !/[a-z]/.test(s) && /[A-Z]/.test(s);
  // A single short ALL-CAPS token is an acronym/brand, never a shouted
  // name — "KBB" stays KBB (Round 10.1, 1C).
  if (allCaps && /^[A-Z0-9&.]{2,5}$/.test(s.trim())) return s.trim();
  return s
    .split(/\s+/)
    .map((w, i) => {
      if (!allCaps && /[A-Z]/.test(w) && /^[A-Z0-9&.]{2,5}$/.test(w)) return w;
      const lw = w.toLowerCase();
      return i > 0 && SMALL_WORDS.has(lw)
        ? lw
        : lw.replace(/(^|-)([a-z])/g, (_m, p: string, c: string) => p + c.toUpperCase());
    })
    .join(" ")
    .replace(/\b(Llc|Inc|Cpd|Ab|Us|Dmv)\b/g, (m) => m.toUpperCase());
}

function countUpper(s: string): number {
  return (s.match(/[A-Z]/g) ?? []).length;
}

function stripPhone(s: string): string {
  // Remove a leading/standalone phone number, keeping any trailing label.
  let out = s.replace(/^[+(]?\d[\d\s().+-]{6,}\)?\s*/, "");
  // Unwrap a parenthetical label: "(CPD Area 3 Property Theft)" → inside.
  const paren = out.match(/^\(([^)]+)\)$/);
  if (paren) out = paren[1]!;
  // Drop any stray unbalanced parens left by the phone strip.
  return out.replace(/^\(+\s*/, "").replace(/\s*\)+$/, "").trim();
}

function lastFirst(s: string): string {
  const m = s.match(/^([A-Za-z'’.-]+),\s*([A-Za-z'’. -]+)$/);
  return m ? `${m[2]!.trim()} ${m[1]!.trim()}` : s;
}

function orgFromEmail(email: string): string | undefined {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) return undefined;
  if (DOMAIN_ORG[domain]) return DOMAIN_ORG[domain];
  if (PERSONAL_DOMAINS.has(domain)) return undefined;
  const root = domain.split(".").slice(0, -1).join(" ").replace(/[-_]/g, " ");
  // A generic-mail domain root is noise, not an organisation:
  // "alex@email.com" must not mint an "Email" party name or org subtitle.
  if (!root || isNonParty(root)) return undefined;
  return titleCase(root);
}

function inferRole(text: string): string | undefined {
  const t = text.toLowerCase();
  if (/police|cpd|sheriff|law enforcement/.test(t)) return "Law enforcement";
  if (/secretary of state|ilsos|dmv|revocation/.test(t)) return "Government";
  if (/liberty mutual|insurance|claims|adjuster|allstate|geico/.test(t)) return "Insurance";
  if (/\btow|towing\b/.test(t)) return "Tow company";
  if (/enterprise|hertz|avis|rental|rent-a-car/.test(t)) return "Rental car provider";
  if (/infiniti|marathon|auto|motors|repair|body shop|dealership/.test(t)) return "Vehicle service";
  if (/amazon|store|shop|retail/.test(t)) return "Retailer";
  return undefined;
}

function levenshtein(a: string, b: string): number {
  if (Math.abs(a.length - b.length) > 3) return 99;
  const dp = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    let prev = dp[0]!;
    dp[0] = j;
    for (let i = 1; i <= a.length; i++) {
      const tmp = dp[i]!;
      dp[i] = Math.min(dp[i]! + 1, dp[i - 1]! + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[a.length]!;
}

function sigTokens(name: string): Set<string> {
  return new Set(
    name.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 4 && !STOP_TOKENS.has(w)),
  );
}

function looksOrg(name: string): boolean {
  // "mutual": insurer-name word ("Liberty Mutual Alt", "Mutual of Omaha") —
  // without it a billing-descriptor variant classifies as a person and can
  // never token-merge with the canonical insurer org (Round 9, 2C).
  return /department|insurance|mutual|police|secretary|state|towing|tow|enterprise|infiniti|marathon|amazon|llc|inc|company|\.com|services?/i.test(name)
    || /^[A-Z]{2,5}$/.test(name);
}

interface Cand { name: string; email: string | null; linkedOrg?: string }

// One raw value → one or more candidates (splitting "Org / Person").
function toCandidates(raw: string): Cand[] {
  const cleaned = raw.replace(/^cc:\s*/i, "").trim().replace(/^["']|["']$/g, "");
  const parts = cleaned.split(/\s*[/|]\s*/).filter(Boolean);
  // An org part can be keyword-detected OR a known alias ("Liberty Mutual").
  const isOrgLike = (p: string) => looksOrg(p) || !!ALIASES[p.toLowerCase()];
  const orgPart = parts.find((p) => isOrgLike(p) && !/@/.test(p));
  const orgCanon = orgPart
    ? ALIASES[orgPart.toLowerCase()] ?? titleCase(stripPhone(orgPart))
    : undefined;
  const out: Cand[] = [];
  for (const part of parts) {
    const m = part.match(/^(.*?)<([^>]+)>/);
    let name = m ? m[1]!.trim() : part.trim();
    let email: string | null = m ? m[2]!.trim().toLowerCase() : null;
    if (!email && /^[^@\s]+@[^@\s]+$/.test(name)) { email = name.toLowerCase(); name = ""; }
    name = lastFirst(stripPhone(name).replace(/^["']|["']$/g, "").trim());
    const linked = orgPart && orgPart !== part && !isOrgLike(part) ? orgCanon : undefined;
    out.push({ name, email, linkedOrg: linked });
  }
  return out;
}

interface Ent {
  name: string;
  isOrg: boolean;
  tokens: Set<string>;
  emails: Set<string>;
  org?: string;
  // Where the org claim came from. Only hard evidence renders:
  //   'metadata' — org named inside the person's own RECORD address value
  //                (header/signature adjacency), or signature/contact-window
  //                adjacency in source text;
  //   'email'    — derived from the person's email domain.
  // Org guesses carried by AI fact-party values are DISCARDED at this
  // layer — extraction may store them, display re-derives.
  orgSource?: "metadata" | "email";
  // Participation evidence for the parties-list gate.
  correspondent: boolean; // appeared in record from/to metadata
  factRecords: Set<string>; // distinct records where it's the attributed party
}

function nameTokens(name: string): string[] {
  return name.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

// Can `shortName` fold into `longName`? Deterministic abbreviation check:
// first tokens must be EQUAL, and every remaining short token must match the
// long name's remaining tokens in order — by equality, by prefix
// ("ins" → "insurance", >= 3 chars), or as an acronym of consecutive tokens
// ("pd" → "police department"). Exported for unit tests.
export function canFoldName(shortName: string, longName: string): boolean {
  if (shortName.length >= longName.length) return false;
  const a = nameTokens(shortName);
  const b = nameTokens(longName);
  if (a.length === 0 || a.length > b.length || a[0] !== b[0]) return false;
  let j = 1;
  outer: for (let i = 1; i < a.length; i++) {
    const t = a[i]!;
    while (j < b.length) {
      const w = b[j]!;
      if (w === t || (t.length >= 3 && w.startsWith(t))) {
        j += 1;
        continue outer;
      }
      if (t.length >= 2 && t.length <= 5 && j + t.length <= b.length) {
        const initials = b.slice(j, j + t.length).map((x) => x[0]).join("");
        if (initials === t) {
          j += t.length;
          continue outer;
        }
      }
      j += 1;
    }
    return false;
  }
  return true;
}

// Fold abbreviated entries into their fuller counterparts, ACROSS the
// person/org classification ("Denver PD" classifies as a person while
// "Denver Police Department" is an org, so the main merge never unites
// them). Conservative: a shorter name folds only when it has >= 2 tokens or
// >= 8 chars; a single first name ("Tom") folds only when EXACTLY one party
// shares that first token. Longest candidate wins.
function foldEnts(ents: Ent[], foldedInto: Map<Ent, Ent>): void {
  let changed = true;
  while (changed) {
    changed = false;
    const shortestFirst = [...ents].sort((x, y) => x.name.length - y.name.length);
    for (const e of shortestFirst) {
      const candidates = ents.filter(
        (o) => o !== e && canFoldName(e.name, o.name),
      );
      if (candidates.length === 0) continue;
      const toks = nameTokens(e.name);
      const conservative = toks.length >= 2 || e.name.length >= 8;
      if (!conservative && candidates.length !== 1) continue;
      const target = candidates.sort((x, y) => y.name.length - x.name.length)[0]!;
      for (const em of e.emails) target.emails.add(em);
      if (e.org && !target.org) { target.org = e.org; target.orgSource = e.orgSource; }
      target.correspondent = target.correspondent || e.correspondent;
      for (const rid of e.factRecords) target.factRecords.add(rid);
      ents.splice(ents.indexOf(e), 1);
      foldedInto.set(e, target);
      changed = true;
      break;
    }
  }
}

// Deterministic participation-evidence windows: signature tails and
// contact-detail windows from source text. Supplied by the
// participation-evidence endpoint (attachment text lives server-side);
// record body tails are derived locally as a fallback.
export interface PartyEvidence {
  windows?: Array<{ record_id: string; text: string }>;
}

const SIG_TAIL_CHARS = 350;

function normText(s: string): string {
  return s.toLowerCase().replace(/\s+/g, " ").trim();
}

// Contact-bearing window: contains a separator-formed phone number or an
// email address — the deterministic shape of a signature/contact block.
// Required for AFFILIATION evidence (not for the participation gate): a
// chat-export tail is the last messages of a conversation, not a signature,
// and narrative co-mention there ("towed from the impound lot to Berman
// Infiniti of Chicago") must not mint an affiliation (Round 9, 2A/2D).
// Bare digit runs (claim numbers) deliberately don't qualify.
const CONTACT_DETAIL_RE =
  /(?:\+?\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}|[^\s@<>()]+@[^\s@<>()]+\.[a-z]{2,}/i;

// Quoted email-header block ("----- Forwarded Message ----- From: X … To:
// Y …") inside a body. It carries contact details, but From/To pairs people
// as CORRESPONDENTS, never as colleagues — when Gordon forwards an AB
// Marathon invoice, the quoted headers name both and must not affiliate
// them (Round 9, 2A: the forwarded-metadata mechanism). Detected by a
// quoted-message marker or any TWO header labels (windows often clip the
// leading "From:").
function isHeaderBlock(w: string): boolean {
  if (/\b(?:forwarded|previous|original) message\b/.test(w)) return true;
  const labels = new Set(
    [...w.matchAll(/\b(from|to|cc|sent|date|subject)\s*:/g)].map((m) => m[1]!),
  );
  return labels.size >= 2;
}

// Index over the derived parties (Round 10): the gated cards PLUS a
// raw-value → canonical-entity mapping, so every surface that renders a
// stored party value can canonicalize the display ("LIBERTY MUTUAL ALT" →
// "Liberty Mutual Insurance") and link to the party. Display-layer only.
export interface PartyIndex {
  cards: PartyCard[];
  // Canonical display name for a raw stored party value; null when the
  // value is a placeholder/non-party (callers fall back to the raw value
  // for per-fact attribution, or drop it from aggregate lists).
  canonicalOf(raw: string | null | undefined): string | null;
  // The gated party card a raw value resolves to, or null (placeholder,
  // or an entity that didn't pass the participation gate).
  cardOf(raw: string | null | undefined): PartyCard | null;
  cardByName(name: string): PartyCard | null;
}

export function deriveParties(
  facts: RecordFact[],
  records: CaseRecord[],
  evidence?: PartyEvidence,
): PartyCard[] {
  return buildPartyIndex(facts, records, evidence).cards;
}

export function buildPartyIndex(
  facts: RecordFact[],
  records: CaseRecord[],
  evidence?: PartyEvidence,
): PartyIndex {
  // Source-tagged raw values: AI fact-party values may carry affiliation
  // GUESSES (e.g. "Officer Daniels (Pinnacle Insurance)") — those decorate
  // the name only; org subtitles require hard evidence, so only
  // record-METADATA raws may contribute an org.
  const raws: Array<{ value: string; source: "meta" | "fact"; recordId: string | null }> = [];
  for (const f of facts)
    if (f.party) raws.push({ value: f.party.trim(), source: "fact", recordId: f.record_id });
  for (const r of records)
    for (const a of [r.from_addr, r.to_addr])
      if (a) raws.push({ value: a, source: "meta", recordId: r.id });

  const ents: Ent[] = [];
  // raw value (lowercased) → the entities its candidates resolved to, and
  // fold redirections, so the index can canonicalize any stored value.
  const rawEnts = new Map<string, Ent[]>();
  const foldedInto = new Map<Ent, Ent>();
  const addEnt = (
    rawName: string,
    email: string | null,
    linkedOrg: string | undefined,
    source: "meta" | "fact",
    recordId: string | null,
  ): Ent | undefined => {
    let name = rawName.trim();
    // Org evidence only from record metadata (header/signature adjacency).
    let org = source === "meta" ? linkedOrg : undefined;
    // Strip a parenthetical — closed or NOT ("Mike Santos (Pinnacle
    // Insurance, Resolution Team" arrives truncated). The content never
    // stays in the display name; it informs the org subtitle ONLY when the
    // value came from record metadata — in an AI fact-party value it is an
    // unverified affiliation claim and is discarded for display.
    const paren = name.match(/^([^(]*[^(\s])\s*\((.*)$/);
    if (paren) {
      name = paren[1]!.trim();
      if (source === "meta" && !org) {
        const content = paren[2]!.replace(/[)\s]+$/, "").trim();
        const orgCand = content.split(",")[0]!.trim();
        if (orgCand && looksOrg(orgCand)) org = titleCase(orgCand);
      }
    }
    // A stripped phone/parenthetical can leave a dangling separator:
    // "Torres Property Management — (555) 218-4490" → trailing "—".
    name = name.replace(/[\s—–·,:-]+$/g, "").trim();
    const aliasKey = name.toLowerCase();
    if (ALIASES[aliasKey]) name = ALIASES[aliasKey]!;
    if (!name && email) {
      const fromDomain = orgFromEmail(email);
      if (fromDomain) name = fromDomain;
      else name = titleCase(email.split("@")[0]!.replace(/[._]/g, " "));
    }
    if (!name || GENERIC.has(name.toLowerCase()) || name.length < 2) {
      if (email && DOMAIN_ORG[email.split("@")[1] ?? ""]) name = DOMAIN_ORG[email.split("@")[1]!]!;
      else return;
    }
    // Channel/placeholder noise, bare emails, and phone numbers are not
    // parties.
    if (isNonParty(name)) return;
    if (PLACEHOLDER_RE.test(name)) return;
    if (/^[^\s@]+@[^\s@]+$/.test(name)) return;
    if (/^[+(]?[\d\s().+-]{7,}$/.test(name)) return;
    if (email && isExcludedEmail(email)) return;
    name = titleCase(name);
    const isOrg = looksOrg(name);
    const tokens = sigTokens(name);

    // Merge: orgs need TWO shared non-generic tokens, OR full containment
    // of one name's significant tokens in the other's with at least one
    // non-generic shared ("Enterprise" ⊆ "Enterprise Rent-A-Car"). One
    // shared token alone must not carry a merge — "Chicago" united the
    // police department with the dealership (Round 10.1, 1B): a place name
    // shared between otherwise-different org names is co-location, not
    // identity. Persons merge by fuzzy name.
    const match = ents.find((e) => {
      if (e.isOrg !== isOrg) return false;
      if (isOrg) {
        // Identical names always unify — short brands ("KBB") have no
        // significant tokens for the token rules to see.
        if (e.name.toLowerCase() === name.toLowerCase()) return true;
        const shared = [...tokens].filter(
          (t) => e.tokens.has(t) && !ORG_GENERIC_TOKENS.has(t),
        );
        if (shared.length >= 2) return true;
        if (shared.length === 0) return false;
        const [small, large] =
          tokens.size <= e.tokens.size ? [tokens, e.tokens] : [e.tokens, tokens];
        return small.size > 0 && [...small].every((t) => large.has(t));
      }
      const a = e.name.toLowerCase(), b = name.toLowerCase();
      return a === b || levenshtein(a, b) <= 3;
    });
    if (match) {
      if (name.length > match.name.length) match.name = name;
      else if (
        name.toLowerCase() === match.name.toLowerCase() &&
        countUpper(name) > countUpper(match.name)
      ) {
        // Same name, better casing ("Denver PD" over "Denver Pd").
        match.name = name;
      }
      // UNION the tokens (never replace): an absorbed variant's identity
      // must stay mergeable, or "Liberty Mutual Alt" can't find "Liberty
      // Mutual Insurance" after a longer name won the display slot.
      for (const t of tokens) match.tokens.add(t);
      if (email) match.emails.add(email);
      if (org && !match.org) { match.org = org; match.orgSource = "metadata"; }
      if (source === "meta") match.correspondent = true;
      else if (recordId) match.factRecords.add(recordId);
      return match;
    } else {
      const ent: Ent = {
        name, isOrg, tokens,
        emails: new Set(email ? [email] : []),
        org,
        orgSource: org ? "metadata" : undefined,
        correspondent: source === "meta",
        factRecords: new Set(source === "fact" && recordId ? [recordId] : []),
      };
      ents.push(ent);
      return ent;
    }
  };

  for (const raw of raws) {
    const rawKey = raw.value.trim().toLowerCase();
    for (const c of toCandidates(raw.value)) {
      const ent = addEnt(c.name, c.email, c.linkedOrg, raw.source, raw.recordId);
      if (ent && rawKey) {
        const list = rawEnts.get(rawKey) ?? [];
        if (!list.includes(ent)) list.push(ent);
        rawEnts.set(rawKey, list);
      }
    }
  }

  // Fold abbreviations / partial names into their fuller counterparts.
  foldEnts(ents, foldedInto);

  // Participation-evidence windows: server-provided (signature tails +
  // contact windows across bodies AND attachments) plus locally-derived
  // record-body tails, so the gate works even without the endpoint.
  const windows: string[] = (evidence?.windows ?? []).map((w) => normText(w.text));
  for (const r of records) {
    for (const t of [r.body_text, r.ocr_text]) {
      const s = (t ?? "").trim();
      if (s) windows.push(normText(s.slice(-SIG_TAIL_CHARS)));
    }
  }
  const inEvidence = (name: string): string | null => {
    const n = normText(name);
    if (n.length < 3) return null;
    for (const w of windows) if (w.includes(n)) return w;
    return null;
  };

  // PARTICIPATION GATE: an entity appears in the parties list only with
  // deterministic participation evidence — correspondent metadata, presence
  // in a signature/contact window, or fact attribution across >= 2 distinct
  // records. Mention-only entities (listing sites cited once in a demand
  // letter) keep their fact-level attribution but mint no party.
  const gated = ents.filter(
    (e) => e.correspondent || e.factRecords.size >= 2 || inEvidence(e.name) !== null,
  );

  // Resolve each person's org, hard evidence only: email domain, org named
  // in the person's own metadata value, or org adjacency inside the same
  // signature/contact window. AI-claimed affiliations never render.
  const orgEnts = gated.filter((e) => e.isOrg);
  const orgNames = orgEnts.map((e) => e.name);
  const buildCard = (e: Ent): PartyCard => {
      let org = e.org;
      let orgSource = e.orgSource;
      if (!org && !e.isOrg) {
        for (const em of e.emails) {
          const o = orgFromEmail(em);
          if (o) { org = o; orgSource = "email"; break; }
        }
      }
      if (!org && !e.isOrg) {
        // Signature-block adjacency: the person and a known org named in
        // the SAME deterministic evidence window — and ONLY a window that
        // carries contact details (the signature/contact-block shape).
        // Plain co-mention in a record tail is narrative, not affiliation.
        const n = normText(e.name);
        if (n.length >= 3) {
          for (const w of windows) {
            if (!w.includes(n) || !CONTACT_DETAIL_RE.test(w) || isHeaderBlock(w))
              continue;
            const adj = orgEnts.find((o) => w.includes(normText(o.name)));
            if (adj) { org = adj.name; orgSource = "metadata"; break; }
          }
        }
      }
      if (org) {
        // Canonicalize to a known org PARTY only on token containment — one
        // shared word ("denver") must not re-affiliate to an unrelated org.
        const ot = sigTokens(org);
        const canon = orgNames.find((n) => {
          const nt = sigTokens(n);
          const [small, large] = ot.size <= nt.size ? [ot, nt] : [nt, ot];
          return small.size > 0 && [...small].every((t) => large.has(t));
        });
        if (canon) org = canon;
      }
      const card: PartyCard = { name: e.name };
      // Render the org subtitle ONLY with hard evidence, and never noise.
      if (org && orgSource && !e.isOrg && !isNonParty(org)) card.org = org;
      const role = inferRole(`${e.name} ${card.org ?? ""} ${[...e.emails].join(" ")}`);
      if (role) card.role = role;
      return card;
  };

  const pairs = gated
    .map((e) => ({ ent: e, card: buildCard(e) }))
    .filter(({ card }) => card.name.length > 1 && !isNonParty(card.name))
    .sort((a, b) => a.card.name.localeCompare(b.card.name));
  const cards = pairs.map((p) => p.card);
  const entCard = new Map(pairs.map((p) => [p.ent, p.card]));

  // Follow fold redirections to the surviving entity.
  const resolveEnt = (e: Ent): Ent => {
    let cur = e;
    while (foldedInto.has(cur)) cur = foldedInto.get(cur)!;
    return cur;
  };
  const entsForRaw = (raw: string | null | undefined): Ent[] => {
    const key = (raw ?? "").trim().toLowerCase();
    if (!key) return [];
    return (rawEnts.get(key) ?? []).map(resolveEnt);
  };
  // For "Org / Person" combined values the PERSON is the actor.
  const primaryEnt = (raw: string | null | undefined): Ent | null => {
    const list = entsForRaw(raw);
    return list.find((e) => !e.isOrg) ?? list[0] ?? null;
  };

  return {
    cards,
    canonicalOf: (raw) => primaryEnt(raw)?.name ?? null,
    cardOf: (raw) => {
      const e = primaryEnt(raw);
      return e ? entCard.get(e) ?? null : null;
    },
    cardByName: (name) => cards.find((c) => c.name === name) ?? null,
  };
}

function isExcludedEmail(email: string): boolean {
  return /@casethread\.(app|com)$/i.test(email);
}

// ---------------------------------------------------------------------------
// Round 10 display helpers built on the index.
// ---------------------------------------------------------------------------

// Canonical, placeholder-free, deduplicated party list for aggregate
// "parties:" lines (rollups, timeline party arrays). Placeholders are
// dropped (1A); remaining values canonicalize through the index (1B) and
// dedupe case-insensitively, preserving first-seen order.
export function formatPartyList(
  raws: Iterable<string>,
  index: Pick<PartyIndex, "canonicalOf">,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of raws) {
    if (isPlaceholderParty(raw)) continue;
    const name = index.canonicalOf(raw) ?? raw.trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

// One adapter threaded through fact cards / commitment rows / rollups so
// party rendering is consistent everywhere: canonical display, tappable
// only when a gated card exists (placeholders are never tappable — there
// is nothing to show), aggregate-list formatting.
export interface PartyUi {
  display(raw: string): string;
  clickable(raw: string): boolean;
  open(raw: string): void;
  list(raws: Iterable<string>): string[];
}

export function makePartyUi(
  index: PartyIndex,
  open: (card: PartyCard) => void,
): PartyUi {
  return {
    display: (raw) => index.canonicalOf(raw) ?? raw,
    clickable: (raw) => !!index.cardOf(raw),
    open: (raw) => {
      const c = index.cardOf(raw);
      if (c) open(c);
    },
    list: (raws) => formatPartyList(raws, index),
  };
}

// Deterministic contact details for a party (Round 10 party panel): record
// metadata addresses naming them, and signature/contact evidence windows
// naming them (quoted email-header blocks excluded — From/To pairs are
// correspondence, not the party's own contact block). Every entry cites
// its source record. NO AI, no guessing — absence renders nothing.
export interface PartyContact {
  kind: "email" | "phone";
  value: string;
  sourceKind: "metadata" | "signature";
  recordId: string | null;
}

const EMAIL_SCAN_RE = /[^\s@<>()]+@[^\s@<>()]+\.[a-z]{2,}/gi;
const PHONE_SCAN_RE =
  /(?:\+?\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/g;

export function partyContacts(
  name: string,
  records: CaseRecord[],
  evidence?: PartyEvidence,
): PartyContact[] {
  const tokens = name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2);
  if (tokens.length === 0) return [];
  // Token match (not substring): record metadata writes "Last, First".
  const mentions = (text: string): boolean => {
    const t = normText(text);
    return tokens.every((tok) => t.includes(tok));
  };

  const out: PartyContact[] = [];
  const seen = new Set<string>();
  const push = (
    kind: PartyContact["kind"],
    value: string,
    sourceKind: PartyContact["sourceKind"],
    recordId: string | null,
  ) => {
    const v = value.trim();
    if (kind === "email" && isExcludedEmail(v)) return;
    const key = `${kind}:${v.toLowerCase().replace(/[^a-z0-9@+.]/g, "")}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind, value: v, sourceKind, recordId });
  };

  // Record metadata: an address value naming the party with an email.
  for (const r of records) {
    for (const a of [r.from_addr, r.to_addr]) {
      if (!a || !mentions(a)) continue;
      EMAIL_SCAN_RE.lastIndex = 0;
      for (const m of a.matchAll(EMAIL_SCAN_RE))
        push("email", m[0]!.toLowerCase(), "metadata", r.id);
    }
  }

  // Signature/contact windows naming the party.
  for (const w of evidence?.windows ?? []) {
    const t = normText(w.text);
    if (!mentions(w.text) || isHeaderBlock(t)) continue;
    EMAIL_SCAN_RE.lastIndex = 0;
    for (const m of w.text.matchAll(EMAIL_SCAN_RE))
      push("email", m[0]!.toLowerCase(), "signature", w.record_id);
    PHONE_SCAN_RE.lastIndex = 0;
    for (const m of w.text.matchAll(PHONE_SCAN_RE))
      push("phone", m[0]!, "signature", w.record_id);
  }

  // Contact windows can clip an email at their leading boundary
  // ("ael.wetherington@…" beside the full address) — drop any email that
  // is a strict suffix of another collected email.
  const emails = out.filter((c) => c.kind === "email").map((c) => c.value);
  const deduped = out.filter(
    (c) =>
      c.kind !== "email" ||
      !emails.some((e) => e !== c.value && e.endsWith(c.value)),
  );

  return deduped.slice(0, 6);
}
